<template>
    <!-- Teleported to <body> with a near-max z-index, same shell as AcceptTosModal:
         rendered inline the overlay is trapped under the homepage hero's stacking
         context, and any z-50 class loses to the cookie-consent banner (z 999997)
         and the JuicyHelp chat widget (z 2147483000), both of which have
         previously intercepted clicks on a modal's own buttons.
         The v-if sits on the <Teleport> so SSR renders nothing at all and there
         is no hydration mismatch. -->
    <Teleport v-if="show" to="body">
    <div class="fixed inset-0 overflow-y-auto bg-black/30 backdrop-blur-sm flex p-4" style="z-index: 2147483001" role="dialog" aria-modal="true" aria-labelledby="bhwOfferTitle" @click.self="dismiss">
        <!-- max-h-full + min-h-0/shrink-0 rather than any fixed height: on a short
             viewport the body scrolls and the CTA stays reachable. -->
        <div class="m-auto w-full max-w-lg max-h-full flex" role="document">
            <div class="w-full min-h-0 flex flex-col rounded-xl shadow-2xl bg-white text-gray-900">
                <div class="shrink-0 flex justify-between items-start gap-3 p-5 pb-3">
                    <h2 id="bhwOfferTitle" class="text-xl font-bold leading-snug">{{ tp('popup_title', { amount: amountLabel }) }}</h2>
                    <button ref="closeButton" type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-xl leading-none p-1.5 inline-flex items-center shrink-0" @click="dismiss" :aria-label="tp('close')">&times;</button>
                </div>

                <div class="p-5 pt-0 min-h-0 overflow-y-auto overscroll-contain">
                    <p class="text-gray-600">{{ code ? tp('popup_body_code', { amount: amountLabel }) : tp('popup_body', { amount: amountLabel }) }}</p>

                    <!-- The anti-spam rule, stated before the visitor spends any effort: the
                         scraper ignores codes posted from forum accounts younger than this,
                         and someone who finds that out only after posting opens a ticket.
                         Hidden at 0, where there is no rule to state. -->
                    <p v-if="accountAgeNote" class="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">{{ accountAgeNote }}</p>

                    <!-- Showing the post inline saves the visitor a navigation. We hand over
                         the finished sentence, not the bare code: the visitor's next action is
                         writing a forum reply, and composing it for them is the difference
                         between one paste and a paste plus improvising. -->
                    <div v-if="code" class="mt-4">
                        <p class="text-sm font-medium text-gray-500 mb-1">{{ tp('your_code') }}</p>
                        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <div class="flex-grow bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 min-w-0">
                                <span class="text-gray-800 break-words text-sm select-all">{{ postMessage }}</span>
                            </div>
                            <button type="button" @click="copyCode" class="flex-shrink-0 bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition cursor-pointer">
                                <font-awesome-icon :icon="faCopy" class="mr-1.5" />
                                {{ copied ? tp('copied') : tp('copy') }}
                            </button>
                        </div>
                    </div>

                    <!-- Inline, never a toast: the vue-toastification container sits at
                         z-index 9999 and would render behind this overlay. -->
                    <p v-if="errorMsg" class="text-red-600 text-sm text-center mt-2">{{ errorMsg }}</p>
                </div>

                <div class="shrink-0 p-5 pt-0 flex flex-col gap-2">
                    <!-- Never opens another modal. LoginModal's overlay is still z-[100],
                         so it would render *under* this one and be unclickable. A plain
                         navigation instead -- see cta for where it goes and why. -->
                    <!-- hover-bg-* (hyphen), not hover:bg-* -- the brand-* colours are hand-written
                         utilities in app.css, so Tailwind v4 silently drops the variant form. -->
                    <a v-if="cta && cta.external" :href="cta.url" target="_blank" rel="noopener noreferrer" @click="engage" class="w-full inline-flex justify-center items-center py-3 px-4 border border-transparent shadow-sm text-sm font-semibold rounded-lg text-white bg-brand-primary hover-bg-brand-primary-dark transition">
                        {{ tp(cta.key) }}
                    </a>
                    <Link v-else-if="cta" :href="cta.url" @click="engage" class="w-full inline-flex justify-center items-center py-3 px-4 border border-transparent shadow-sm text-sm font-semibold rounded-lg text-white bg-brand-primary hover-bg-brand-primary-dark transition">
                        {{ tp(cta.key) }}
                    </Link>
                    <button type="button" @click="dismiss" class="w-full py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
                        {{ tp('popup_dismiss') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { useModalRegistration } from '@/composables/useModalRegistry';
import { activePromo, promoMessageGroup } from '@/composables/useBhwPopup';

const props = defineProps({
    show: {
        type: Boolean,
        required: true,
    },
    /**
     * Which campaign this popup is offering. Optional: usePromoPopup already
     * publishes the campaign it resolved, so a layout that mounts this modal
     * without wiring the prop still shows the right one.
     *
     * EXACTLY ONE instance of this modal is mounted, in AppLayout, and it swaps
     * campaign rather than there being one modal per campaign. useModalRegistry
     * counts any open modal of ours and usePromoPopup refuses to schedule while
     * one is open, so a second instance would let whichever mounted first
     * permanently suppress the other.
     */
    campaign: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['close', 'engage']);

const page = usePage();
const { t, te } = useI18n();

const closeButton = ref(null);
const copied = ref(false);
const errorMsg = ref('');

// Guests get no per-user promo props at all -- they are null for everyone who is
// not logged in, which is what keeps per-visitor state out of edge-cacheable
// HTML. The live amount for a guest comes from the status endpoint, via the
// campaign the composable resolved; this is only the last-resort default.
const DEFAULT_AMOUNT_EUR = '3.00';
// Likewise for the forum account age: what the scraper enforces when the server
// does not say, so the popup never claims a looser rule than the one applied.
const DEFAULT_MIN_ACCOUNT_AGE_MONTHS = 3;

/**
 * The campaign on screen.
 *
 * Falls all the way back to BHW's pre-multi-campaign shape rather than to
 * nothing: a bound prop, then the campaign the composable resolved, then the
 * legacy shared prop. The offer popup is the promo's only surface, so "render
 * nothing" is never the better failure.
 */
const promo = computed(() => props.campaign ?? activePromo.value ?? {
    slug: 'bhw',
    name: 'BlackHatWorld',
    amountEur: page.props.bhw?.amountEur ?? DEFAULT_AMOUNT_EUR,
    threadUrl: page.props.bhw?.threadUrl ?? null,
    entryUrl: '/bhw',
    status: page.props.bhw?.status ?? null,
    code: page.props.bhw?.code ?? null,
});

/**
 * Campaign-scoped copy. BHW renders the frozen `bhw` group so its English is
 * byte-identical to the day it launched; every other campaign renders the
 * generic `promo` group, which says {forum} everywhere BHW's says BlackHatWorld.
 */
const tp = (key, params = {}) => t(`${promoMessageGroup(promo.value)}.${key}`, { forum: promo.value.name, ...params });

const code = computed(() => promo.value.code ?? null);
const threadUrl = computed(() => promo.value.threadUrl ?? null);
const amountLabel = computed(() => `€${promo.value.amountEur ?? DEFAULT_AMOUNT_EUR}`);
const accountAgeMonths = computed(() => Number(promo.value.minForumAccountAgeMonths ?? DEFAULT_MIN_ACCOUNT_AGE_MONTHS));
// Not through tp(): "1 month" / "3 months" is a plural message, and vue-i18n
// picks the form from the count passed as the third argument.
//
// NULL when the key is missing, which hides the note: translations are cached
// forever per locale, and until deploy:refresh-caches has run, a release that
// adds a key would otherwise print 'bhw.account_age_note' in a warning box.
const accountAgeNote = computed(() => {
    const key = `${promoMessageGroup(promo.value)}.account_age_note`;
    if (accountAgeMonths.value <= 0 || !te(key)) {
        return null;
    }
    return t(key, { forum: promo.value.name, months: accountAgeMonths.value }, accountAgeMonths.value);
});

// The ready-to-paste forum reply. Built here rather than server-side so it stays
// out of any edge-cacheable HTML, and copied verbatim by copyCode() so what the
// visitor pastes is exactly what they were shown.
const postMessage = computed(() => tp('post_template', { amount: amountLabel.value, code: code.value }));

// Logged in: the entry route mints the code and bounces straight back to the
// homepage, where this popup reopens with the code filled in -- so it is the CTA
// even for someone who already has one. A guest has nothing to mint yet, and the
// entry route would only bounce them back to this same popup, so they go to the
// signup page; the session keys the entry route already stored bring them back
// through the mint on their way out of RegisterController (or LoginController,
// for the many who already have accounts and use the "sign in" link on that page).
//
/**
 * The one action that actually moves this visitor forward, which is a different
 * action in each of the three states the popup can open in. The popup never opens
 * on a credited/revoked/expired claim (usePromoPopup suppresses those), so those
 * states need no button here.
 *
 * The entry path is a literal from the campaign row, not route(): /bhw is
 * deliberately English-only with no /{locale}/bhw twin, so this is exactly what
 * route() would produce and does not depend on ziggy.js having been regenerated
 * before this component renders.
 */
const cta = computed(() => {
    if (! page.props.auth?.user) {
        return { url: '/register', key: 'popup_cta_register', external: false };
    }

    // No code yet: the campaign's entry route is the only mint point, so this is
    // the one path that actually issues one.
    if (! code.value) {
        return { url: promo.value.entryUrl ?? '/bhw', key: 'popup_cta', external: false };
    }

    // The code is already on screen with a copy button, so "Get my code" here
    // would point at the entry route to mint a code they are looking at. The
    // remaining step is the forum post. Null when no thread URL is configured yet
    // rather than a button that goes nowhere -- the copy box is still the whole point.
    return threadUrl.value ? { url: threadUrl.value, key: 'open_thread', external: true } : null;
});

useModalRegistration(() => props.show);

const dismiss = () => emit('close');
const engage = () => emit('engage');

const copyCode = async () => {
    try {
        await navigator.clipboard.writeText(postMessage.value);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 2000);
    } catch (err) {
        // The message is rendered select-all right above, so this is recoverable.
        errorMsg.value = tp('copy_failed');
    }
};

const onKeydown = (e) => {
    if (e.key === 'Escape') {
        dismiss();
    }
};

// Focus moves into the dialog on open and back to wherever it was on close, and
// the page behind stops scrolling while the overlay is up.
let previouslyFocused = null;

watch(() => props.show, async (open) => {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.classList.toggle('overflow-hidden', open);

    if (open) {
        previouslyFocused = document.activeElement;
        document.addEventListener('keydown', onKeydown);
        await nextTick();
        closeButton.value?.focus();
    } else {
        document.removeEventListener('keydown', onKeydown);
        errorMsg.value = '';
        previouslyFocused?.focus?.();
        previouslyFocused = null;
    }
});

onBeforeUnmount(() => {
    if (typeof document === 'undefined') {
        return;
    }
    document.removeEventListener('keydown', onKeydown);
    document.documentElement.classList.remove('overflow-hidden');
});
</script>
